<template>
    <a :href="`#filter-collapse-${filter.id}`" class="filter-headline d-flex align-items-center justify-content-between w-100" 
       data-bs-toggle="collapse" role="button" aria-expanded="false" :aria-controls="`filter-collapse-${filter.id}`">
        <span class="filter-title">{{ filter.translation_string_ident }}</span>
        <i class="bi" :class="hasActiveFilters() ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
    </a>
    <div class="collapse" :id="`filter-collapse-${filter.id}`" :class="{ show: hasActiveFilters() }">
        <div class="filter-options py-2">
            <div v-for="value in filter.values" :key="value.filter_value_slug" class="filter-check-label-wrap">
                <div class="form-check d-flex align-items-center">
                    <input type="checkbox" class="form-check-input" 
                           :id="`filter-${value.internal_value}`" 
                           :value="value.filter_value_slug" 
                           :checked="isChecked(value.filter_value_slug)" 
                           @change="toggleFilter($event, value.filter_value_slug)" />
                    <label class="form-check-label ms-2" :for="`filter-${value.internal_value}`">
                        {{ value.displayed_value }}
                    </label>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { onMounted, ref, computed } from 'vue';
    import { getActiveFilters } from '@/Components/FilterManager.vue';

    const props = defineProps(['filter', 'urlParams']);
    const emit = defineEmits(['filterUpdated']);

    // Current selected values for this filter
    const selectedValues = computed(() => {
        if (!props.urlParams) return [];
        const values = props.urlParams.get(props.filter.id);
        return values ? values.split(',') : [];
    });

    function hasActiveFilters() {
        return selectedValues.value.length > 0;
    }

    function isChecked(value) {
        return selectedValues.value.includes(value);
    }

    function toggleFilter(event, value) {
        const isChecked = event.target.checked;
        
        // Get all active filters from URL
        const allActiveFilters = [];
        
        // Add existing filters from URL
        for (const [key, value] of props.urlParams.entries()) {
            if (key !== 'sort' && key !== 'page') {
                const values = value.split(',').filter(v => v.trim() !== '');
                if (values.length > 0) {
                    allActiveFilters.push({
                        id: key,
                        values: values
                    });
                }
            }
        }
        
        // Get current filter from the list or create it
        let currentFilter = allActiveFilters.find(f => f.id === props.filter.id);
        if (!currentFilter) {
            currentFilter = { id: props.filter.id, values: [] };
            allActiveFilters.push(currentFilter);
        }
        
        // Update current filter's values
        if (isChecked) {
            if (!currentFilter.values.includes(value)) {
                currentFilter.values.push(value);
            }
        } else {
            currentFilter.values = currentFilter.values.filter(v => v !== value);
        }
        
        // Remove filter if it has no values
        if (currentFilter.values.length === 0) {
            const index = allActiveFilters.findIndex(f => f.id === props.filter.id);
            if (index !== -1) {
                allActiveFilters.splice(index, 1);
            }
        }
        
        // Notify parent
        emit('filterUpdated', allActiveFilters);
    }
</script>

<style scoped>
    .filter-headline {
        padding: 0.45rem 0.45rem;
        background-color: #e9f0f8;
        border-radius: 0.375rem;
        color: #1E3A8A;
        font-weight: 600;
        transition: all 0.2s ease;
        margin-bottom: 0.25rem;
        border-left: 3px solid #1E3A8A;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        text-decoration: none;
        line-break: anywhere;
    }

    .filter-headline:hover, .filter-headline:focus {
        background-color: #d8e6f6;
        color: #1E3A8A;
        text-decoration: none;
    }
    
    .filter-title {
        font-size: 0.95rem;
        text-transform: capitalize;
        letter-spacing: 0.01em;
    }

    .filter-check-label-wrap {
        margin-bottom: 0.35rem;
        padding: 0.25rem 0.75rem;
        border-radius: 0.25rem;
        transition: background-color 0.15s ease;
    }
    
    .filter-check-label-wrap:hover {
        background-color: #f0f4f8;
    }
    
    .filter-options {
        padding: 0.5rem;
        background-color: #fff;
        border-radius: 0 0 0.375rem 0.375rem;
        border: 1px solid #e5e7eb;
        border-top: none;
        margin-bottom: 0.5rem;
    }

    .form-check {
        min-height: unset;
        margin-bottom: 0;
    }

    .form-check-input {
        width: 1.1rem;
        height: 1.1rem;
        margin-top: 0;
        cursor: pointer;
        border: 2px solid #adb5bd;
        background-color: #fff;
    }

    .form-check-input:checked {
        background-color: #1E3A8A;
        border-color: #1E3A8A;
    }

    .form-check-label {
        font-size: 0.9rem;
        color: #333;
        cursor: pointer;
        user-select: none;
        margin-bottom: 0;
    }

    /* Mobile-friendly adjustments */
    @media (max-width: 767.98px) {
        .form-check-input {
            width: 1.25rem;
            height: 1.25rem;
            margin-right: 0.5rem;
        }
        
        .form-check-label {
            font-size: 0.95rem;
            padding: 0.25rem 0;
        }
        
        .filter-check-label-wrap {
            padding: 0.5rem 0.75rem;
        }
        
        .filter-headline {
            background-color: #d8e6f6;
        }
    }
</style>
